// Winzige Synth-Toene — kein Audio-Asset, nur WebAudio.
//
// Bewusst **nicht** ueber Phasers Audio-System: das erwartet geladene Dateien,
// und Far Mining hat keine. Toene sind kurz und leise gehalten; ein
// Inkrementalspiel laeuft nebenbei.
//
// Die Ton-Einstellung ist persistent, aber ihr Speicherzugriff liegt **nicht
// hier** — Persistenz gehoert ausschliesslich in `state/store.ts` (Gate FG-28).

type Ctx = AudioContext;

let ctx: Ctx | null = null;
let master: GainNode | null = null;
let muted = false;
const cooldown: Record<string, number> = {};

const VOLUME = 0.22;

function ensure(): Ctx | null {
  if (ctx) return ctx;
  const AC = globalThis.AudioContext ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  master = ctx.createGain();
  master.gain.value = muted ? 0 : VOLUME;
  master.connect(ctx.destination);
  return ctx;
}

/** Nach der ersten Nutzereingabe aufrufen (Autoplay-Richtlinie der Browser). */
export function unlockAudio(): void {
  const c = ensure();
  if (c && c.state === 'suspended') void c.resume();
}

export function setMuted(value: boolean): void {
  muted = value;
  if (master) master.gain.value = value ? 0 : VOLUME;
}

export const isMuted = (): boolean => muted;

/** Gedrosselt: derselbe Ton klingt hoechstens alle `ms` Millisekunden. */
function throttled(key: string, ms: number): boolean {
  const now = performance.now();
  if (cooldown[key] && now - cooldown[key] < ms) return false;
  cooldown[key] = now;
  return true;
}

interface ToneOptions {
  freq?: number;
  to?: number;
  dur?: number;
  type?: OscillatorType;
  gain?: number;
  delay?: number;
}

function tone({ freq = 440, to = freq, dur = 0.12, type = 'square', gain = 0.5, delay = 0 }: ToneOptions): void {
  const c = ensure();
  if (!c || !master || muted) return;
  const t0 = c.currentTime + delay;
  const osc = c.createOscillator();
  const amp = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (to !== freq) osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), t0 + dur);
  amp.gain.setValueAtTime(0.0001, t0);
  amp.gain.exponentialRampToValueAtTime(gain, t0 + 0.008);
  amp.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(amp);
  amp.connect(master);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

function noise({ dur = 0.25, gain = 0.4, freq = 900 }: { dur?: number; gain?: number; freq?: number }): void {
  const c = ensure();
  if (!c || !master || muted) return;
  const len = Math.floor(c.sampleRate * dur);
  const buffer = c.createBuffer(1, len, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
  const src = c.createBufferSource();
  src.buffer = buffer;
  const filter = c.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = freq;
  const amp = c.createGain();
  amp.gain.value = gain;
  src.connect(filter);
  filter.connect(amp);
  amp.connect(master);
  src.start();
}

export const sfx = {
  laser(): void {
    if (throttled('laser', 110)) tone({ freq: 900, to: 420, dur: 0.06, type: 'square', gain: 0.08 });
  },
  hit(): void {
    if (throttled('hit', 70)) tone({ freq: 260, to: 150, dur: 0.05, type: 'triangle', gain: 0.12 });
  },
  crack(): void {
    if (throttled('crack', 55)) noise({ dur: 0.22, gain: 0.16, freq: 1200 });
  },
  pickup(): void {
    if (throttled('pickup', 70)) tone({ freq: 1180, to: 1600, dur: 0.05, type: 'sine', gain: 0.1 });
  },
  gem(): void {
    tone({ freq: 1200, dur: 0.1, type: 'sine', gain: 0.2 });
    tone({ freq: 1800, dur: 0.14, type: 'sine', gain: 0.16, delay: 0.06 });
  },
  upgrade(): void {
    tone({ freq: 520, dur: 0.09, type: 'square', gain: 0.22 });
    tone({ freq: 780, dur: 0.12, type: 'square', gain: 0.2, delay: 0.07 });
  },
  level(): void {
    [523, 659, 784, 1046].forEach((f, i) =>
      tone({ freq: f, dur: 0.16, type: 'triangle', gain: 0.2, delay: i * 0.07 }));
  },
  quest(): void {
    [660, 880].forEach((f, i) => tone({ freq: f, dur: 0.14, type: 'square', gain: 0.2, delay: i * 0.09 }));
  },
  ping(): void {
    // Das Spieler-Signal: zwei kurze, helle Toene — Sonar, nicht Alarm.
    tone({ freq: 880, dur: 0.09, type: 'sine', gain: 0.14 });
    tone({ freq: 1320, dur: 0.12, type: 'sine', gain: 0.1, delay: 0.08 });
  },
  warp(): void {
    tone({ freq: 120, to: 1400, dur: 1.1, type: 'sawtooth', gain: 0.22 });
    noise({ dur: 1.0, gain: 0.14, freq: 700 });
  },
  boom(): void {
    noise({ dur: 0.5, gain: 0.28, freq: 500 });
    tone({ freq: 90, to: 40, dur: 0.4, type: 'sine', gain: 0.3 });
  },
};
